import { semanticStaticPaths } from '../semantic-content/pages.js';

import type { SsrRouteId } from '../../types/ssr.js';

const SEMANTIC_PATH_SET = new Set(semanticStaticPaths);

export const PUBLIC_SSR_ROUTE_IDS: SsrRouteId[] = [
  'home',
  'products',
  'product-detail',
  'news',
  'forums',
  'bugs',
  'partnership',
  'docs',
  'downloads',
  'semantic',
];

const STATIC_ROUTE_IDS = new Set<SsrRouteId>(['home', 'products', 'product-detail', 'partnership', 'docs', 'downloads', 'semantic']);
const DYNAMIC_ROUTE_IDS = new Set<SsrRouteId>(['news', 'forums', 'bugs']);

export function normalizeRoutePath(pathname: string) {
  if (!pathname || pathname === '') {
    return '/';
  }

  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

export function matchSsrRoute(pathname: string): { routeId: SsrRouteId | null; params: Record<string, string> } {
  const normalizedPath = normalizeRoutePath(pathname);

  if (normalizedPath === '/') {
    return { routeId: 'home', params: {} };
  }

  if (normalizedPath === '/products') {
    return { routeId: 'products', params: {} };
  }

  const productMatch = normalizedPath.match(/^\/products\/([^/]+)$/);
  if (productMatch) {
    return {
      routeId: 'product-detail',
      params: { productId: decodeURIComponent(productMatch[1]) },
    };
  }

  if (normalizedPath === '/news') {
    return { routeId: 'news', params: {} };
  }

  if (normalizedPath === '/forums') {
    return { routeId: 'forums', params: {} };
  }

  if (normalizedPath === '/bugs') {
    return { routeId: 'bugs', params: {} };
  }

  if (normalizedPath === '/partnership') {
    return { routeId: 'partnership', params: {} };
  }

  if (normalizedPath === '/downloads') {
    return { routeId: 'downloads', params: {} };
  }

  if (normalizedPath === '/docs') {
    return { routeId: 'docs', params: {} };
  }

  if (SEMANTIC_PATH_SET.has(normalizedPath)) {
    return { routeId: 'semantic', params: {} };
  }

  const docsAppMatch = normalizedPath.match(/^\/docs\/apps\/([^/]+)$/);
  if (docsAppMatch) {
    return {
      routeId: 'semantic',
      params: { docSlug: decodeURIComponent(docsAppMatch[1]) },
    };
  }

  const docsConceptMatch = normalizedPath.match(/^\/docs\/concepts\/([^/]+)$/);
  if (docsConceptMatch) {
    return {
      routeId: 'semantic',
      params: { conceptSlug: decodeURIComponent(docsConceptMatch[1]) },
    };
  }

  const docsUseCaseMatch = normalizedPath.match(/^\/docs\/use-cases\/([^/]+)$/);
  if (docsUseCaseMatch) {
    return {
      routeId: 'semantic',
      params: { useCaseSlug: decodeURIComponent(docsUseCaseMatch[1]) },
    };
  }

  const docsSectionMatch = normalizedPath.match(/^\/docs\/([^/]+)$/);
  if (docsSectionMatch) {
    return {
      routeId: 'docs',
      params: { sectionId: decodeURIComponent(docsSectionMatch[1]) },
    };
  }

  return { routeId: null, params: {} };
}

export function isPublicSsrPath(pathname: string) {
  return matchSsrRoute(pathname).routeId !== null;
}

export function isMapleAppPath(pathname: string) {
  const normalizedPath = normalizeRoutePath(pathname);
  return normalizedPath.startsWith('/start') || normalizedPath.startsWith('/desktop/start');
}

export function getRouteCacheControl(routeId: SsrRouteId | null) {
  if (!routeId) {
    return 'no-store';
  }

  if (STATIC_ROUTE_IDS.has(routeId)) {
    return 'public, max-age=300, s-maxage=3600, stale-while-revalidate=86400';
  }

  if (DYNAMIC_ROUTE_IDS.has(routeId)) {
    return 'public, max-age=60, s-maxage=300, stale-while-revalidate=600';
  }

  return 'public, max-age=120, s-maxage=600, stale-while-revalidate=1800';
}

export function getRouteCacheTtlMs(routeId: SsrRouteId | null) {
  if (!routeId) {
    return 0;
  }

  return STATIC_ROUTE_IDS.has(routeId) ? 5 * 60 * 1000 : 60 * 1000;
}

export function getSsrStaticPaths() {
  return [
    '/',
    '/products',
    '/downloads',
    '/partnership',
    ...semanticStaticPaths,
  ];
}
